import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { tagsOf } from '../../lib/memoryTags';
import { IPlay } from './DashIcons';

// MME Playground — a live window into the retrieval pipeline.
//
// The user types a prompt, and we fire /memory/inject?debug=true. The
// response carries the full reasoning trace: seed tags, activated tags
// (after propagation), candidate count, scored count, and the per-item
// score breakdown for whatever ends up in the pack. This is the page
// that makes the architecture visible — most memory layers can't show
// you any of this because vector cosine doesn't decompose into legible
// components. Tag-graph + IDF does.

const DEFAULT_LIMIT = 5;
const DEFAULT_BUDGET = 2048;
const PROMPT_PRESETS = [
  'mongodb persistent store memory blocks',
  'concurrent save behavior memory engine',
  'sarah chen frontier ventures pre-seed investor',
  'go fiber api server redis rate limiting backend',
];

const PlaygroundPage = () => {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [budget, setBudget] = useState(DEFAULT_BUDGET);
  const [lastQuery, setLastQuery] = useState(null);

  const inject = useMutation({
    mutationFn: (body) => apiService.injectMemory(body),
    onError: (err) => {
      toast.error(err?.response?.data?.error || err?.message || 'Inject failed');
    },
  });

  const run = () => {
    const p = prompt.trim();
    if (!p) {
      toast('Type a prompt first', { icon: '·' });
      return;
    }
    setLastQuery({ prompt: p, limit, budget });
    inject.mutate({
      prompt: p,
      limit,
      tokenBudget: budget,
      debug: true,
    });
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl-Enter submits — common pattern for textarea forms.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      run();
    }
  };

  const data = inject.data;
  const items = data?.items || [];
  const seedTags = data?.seedTags || [];
  const activatedTags = data?.debug?.activatedTags || [];
  const candidatesCount = data?.debug?.candidatesCount ?? 0;
  const scoredCount = data?.debug?.scoredCount ?? 0;
  const selectedCount = data?.debug?.selectedCount ?? 0;
  const propagationOn = data?.debug?.propagationEnabled !== false;
  const totalTokens = data?.totalTokens ?? 0;

  // Show the most-activated tags first when displaying the activated set
  // so the visual emphasis matches what the scorer is weighting.
  const activatedSorted = useMemo(() => {
    if (!Array.isArray(activatedTags)) return [];
    // activatedTags is a string[] (just labels) — show first 30.
    return activatedTags.slice(0, 30);
  }, [activatedTags]);

  return (
    <>
      <Helmet><title>Playground · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>Playground</h1>
          <div className="sub">
            Run any prompt through MME's retrieval pipeline. Every seed tag,
            every activated edge, every score component shows up below — the
            same reasoning trace the production inject endpoint produces.
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-body">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a prompt — e.g. 'what did I have for dinner Tuesday' or 'mongodb persistent store'"
              rows={3}
              style={{
                width: '100%',
                fontFamily: 'inherit',
                fontSize: 14,
                padding: 12,
                border: '1px solid var(--line)',
                borderRadius: 6,
                resize: 'vertical',
                background: 'var(--bg, #fff)',
                color: 'inherit',
              }}
              aria-label="Prompt"
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                marginTop: 12,
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={run}
                disabled={inject.isPending || !user}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <IPlay size={14} />
                {inject.isPending ? 'Running…' : 'Run inject'}
                <span className="kbd" style={{ marginLeft: 4 }}>⌘↵</span>
              </button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--mono)' }}>limit</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value, 10) || DEFAULT_LIMIT)}
                  style={{ width: 56, padding: '4px 6px', border: '1px solid var(--line)', borderRadius: 4 }}
                />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--mono)' }}>tokenBudget</span>
                <input
                  type="number"
                  min={128}
                  max={16000}
                  step={128}
                  value={budget}
                  onChange={(e) => setBudget(parseInt(e.target.value, 10) || DEFAULT_BUDGET)}
                  style={{ width: 80, padding: '4px 6px', border: '1px solid var(--line)', borderRadius: 4 }}
                />
              </label>
              <div style={{ flex: 1, minWidth: 200, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-4)' }}>
                presets:&nbsp;
                {PROMPT_PRESETS.map((p, i) => (
                  <React.Fragment key={p}>
                    <button
                      type="button"
                      onClick={() => setPrompt(p)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ink-3)',
                        textDecoration: 'underline dotted',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 11,
                        padding: 0,
                      }}
                    >
                      "{p.slice(0, 24)}{p.length > 24 ? '…' : ''}"
                    </button>
                    {i < PROMPT_PRESETS.length - 1 && ' · '}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results — only render once we have data */}
        {!data && !inject.isPending && (
          <div className="panel">
            <div className="empty" style={{ padding: '40px 0' }}>
              <div className="title">No run yet</div>
              <div>Type a prompt and hit Run. The full retrieval trace lands here.</div>
            </div>
          </div>
        )}

        {inject.isPending && (
          <div className="panel">
            <div className="empty" style={{ padding: '40px 0' }}>
              <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Running inject…
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Pipeline summary — KPIs that map to algorithm outputs */}
            <div className="kpi-row">
              <div className="kpi">
                <div className="lbl">Seed tags</div>
                <div className="val">{seedTags.length}</div>
                <div className="delta">extracted from prompt</div>
              </div>
              <div className="kpi">
                <div className="lbl">Activated tags</div>
                <div className="val">{activatedTags.length}</div>
                <div className="delta">
                  {propagationOn ? 'after propagation' : 'propagation off'}
                </div>
              </div>
              <div className="kpi">
                <div className="lbl">Candidates</div>
                <div className="val">{candidatesCount}</div>
                <div className="delta">{scoredCount} scored</div>
              </div>
              <div className="kpi">
                <div className="lbl">Pack</div>
                <div className="val">{selectedCount}</div>
                <div className="delta">{totalTokens}t / {lastQuery?.budget || budget}t</div>
              </div>
            </div>

            <div className="two-col">
              {/* Pack items (left) */}
              <div className="panel">
                <div className="panel-head">
                  <h3>Pack</h3>
                  <span className="meta">{items.length} items · sorted by total score</span>
                </div>
                <div className="panel-body" style={{ padding: 0 }}>
                  {items.length === 0 && (
                    <div className="empty" style={{ padding: '28px 16px' }}>
                      The retriever returned zero items. Either the prompt
                      didn&apos;t bridge to any saved memories, or all
                      candidates fell below the abstain threshold. The model
                      will refuse to answer rather than hallucinate.
                    </div>
                  )}
                  {items.map((it, idx) => {
                    const score = it.score || {};
                    const tags = tagsOf(it);
                    return (
                      <div
                        key={it.id || idx}
                        style={{
                          padding: '14px 16px',
                          borderBottom: idx < items.length - 1 ? '1px solid var(--line)' : 'none',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--mono)',
                              color: 'var(--ink-4)',
                              fontSize: 11,
                            }}
                          >
                            #{idx + 1}
                          </span>
                          <strong style={{ flex: 1, fontSize: 14 }}>
                            {it.title || (it.excerpt || '').slice(0, 60) || it.id}
                          </strong>
                          <span
                            style={{
                              fontFamily: 'var(--mono)',
                              fontSize: 12,
                              color: 'var(--ink-3)',
                            }}
                          >
                            {(score.total ?? 0).toFixed(3)}
                          </span>
                        </div>
                        {it.excerpt && (
                          <div
                            style={{
                              fontSize: 13,
                              color: 'var(--ink-3)',
                              marginBottom: 8,
                              lineHeight: 1.5,
                            }}
                          >
                            {it.excerpt.length > 220 ? `${it.excerpt.slice(0, 220)}…` : it.excerpt}
                          </div>
                        )}
                        <div style={{ marginBottom: 8 }}>
                          {tags.slice(0, 6).map((t) => (
                            <span key={t} className="tag">
                              {t}
                            </span>
                          ))}
                          {tags.length > 6 && (
                            <span className="tag">+{tags.length - 6}</span>
                          )}
                        </div>
                        {/* Score component bars */}
                        {[
                          { k: 'activation', v: score.activation },
                          { k: 'recency', v: score.recency },
                          { k: 'importance', v: Math.min((score.importance ?? 0) / 5, 1), raw: score.importance },
                          { k: 'status', v: score.statusBonus },
                          { k: 'diversity penalty', v: score.diversityPenalty, kind: 'penalty' },
                        ].map((row) => {
                          const pct = Math.max(0, Math.min(1, row.v ?? 0)) * 100;
                          return (
                            <div key={row.k} className="rat-row" style={{ fontSize: 11 }}>
                              <span className="k">{row.k}</span>
                              <div className="bar">
                                <div
                                  style={{
                                    width: `${pct}%`,
                                    background: row.kind === 'penalty' ? 'var(--accent, #c0382c)' : undefined,
                                  }}
                                />
                              </div>
                              <span className="v">
                                {(row.raw !== undefined ? row.raw : row.v ?? 0).toFixed(2)}
                              </span>
                            </div>
                          );
                        })}
                        <div
                          style={{
                            marginTop: 8,
                            fontFamily: 'var(--mono)',
                            fontSize: 11,
                            color: 'var(--ink-4)',
                          }}
                        >
                          {it.tokenCost ?? 0}t · id: {(it.id || '').slice(-12)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pipeline trace (right) */}
              <div className="panel">
                <div className="panel-head">
                  <h3>Pipeline trace</h3>
                  <span className="meta">debug=true</span>
                </div>
                <div className="panel-body">
                  <div className="drawer-eyebrow-sm">Seed tags</div>
                  <div style={{ marginBottom: 14 }}>
                    {seedTags.length === 0 ? (
                      <span className="tag">none extracted</span>
                    ) : (
                      seedTags.map((t) => (
                        <span key={t} className="tag hot">
                          {t}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="drawer-eyebrow-sm">
                    Activated tags
                    {activatedTags.length > 30 && (
                      <span style={{ marginLeft: 8, color: 'var(--ink-4)', fontSize: 11 }}>
                        showing 30 of {activatedTags.length}
                      </span>
                    )}
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    {activatedSorted.length === 0 ? (
                      <span className="tag">no propagation</span>
                    ) : (
                      activatedSorted.map((t) => (
                        <span key={t} className="tag">
                          {t}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="drawer-eyebrow-sm">Filters & bounds</div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 12,
                      color: 'var(--ink-3)',
                      lineHeight: 1.7,
                    }}
                  >
                    <div>org: <code>{data.debug?.orgId || '—'}</code></div>
                    <div>user: <code>{data.debug?.userId || '—'}</code></div>
                    <div>budget: {lastQuery?.budget || budget} tokens</div>
                    <div>limit: {lastQuery?.limit || limit}</div>
                    <div>propagation: {propagationOn ? 'on' : 'off'}</div>
                    <div>packId: <code>{(data.packId || '').slice(-12) || '—'}</code></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PlaygroundPage;
